import React, { Component, Fragment } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import Raw from 'shared/components/presentational/Raw/Raw';
import Loader from 'shared/components/presentational/Loader';

const GlobalStyle = createGlobalStyle`
  #faq {
    margin-top: 0;
    margin-bottom: 0;
  }
  
  #faq-error {
    margin-top: 50px;
    margin-bottom: 50px;
    text-align: center;
  }

  #faq .homepage__header {
    z-index: 0 !important;
  }

  #faq .homepage__content {
    position: relative;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  padding-bottom: 50vh;
  height: 90vh;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

export interface Props {
  html?: string;
  loading: boolean;
}

export interface Sdk {
  executeApps: () => () => void;
}

export default class FAQ extends Component<Props> {
  public dispose?: () => void;

  private cookiesConsentCallbackBound = false;

  private callback = (sdk: Sdk) => {
    /**
     * Stop running apps and restart
     */
    if (this.dispose) {
      this.dispose();
    }

    this.dispose = sdk.executeApps();
  };

  private loadScriptsIfNeeded = () => {
    /**
     * SDK already loaded on page
     *
     * Because all FAQ links are triggering page refresh
     * the only possible case to enter this statement is by clicking an faq link
     * in the header while you are already navigating on an faq page
     */
    if ((window as any).FAQ_SDK) {
      this.callback((window as any).FAQ_SDK);
    } else {
      /**
       * loadFAQJS is available
       *
       * It will be available on first load because of SSR
       * since SSR will return normal html so all scripts will be interpreted as normal
       */
      if ((window as any).loadFAQJS) {
        (window as any).loadFAQJS(this.callback);
      } else {
        /**
         * loadFAQJS is not available,
         *
         * Script tags are not interpreted with `dangerouslySetInnerHTML`.
         * it behave like `element.innerHtml`
         * So we need to eval script tags by ourselves.
         *
         *
         * To read: https://stackoverflow.com/questions/2592092/executing-script-elements-inserted-with-innerhtml
         */
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(this.props.html!, 'text/html');

        Array.from(htmlDoc.scripts).forEach(script => {
          /**
           * We only eval scripts tag containing raw javascript
           */
          if (
            script.type !== 'application/ld+json' &&
            !script.src &&
            script.innerText
          ) {
            /**
             * As the FAQ project carries its own GTM container,
             * we try to detect the script tag related to the loading of GTM.
             * TODO: Add an id on the script tag on the FAQ project's end and rely on it to detect the GTM tag
             */
            const isGTMScript = script.innerText.includes(
              'https://www.googletagmanager.com/gtm.js'
            );

            if (isGTMScript) {
              if (
                window.waitForUserConsent &&
                !this.cookiesConsentCallbackBound
              ) {
                this.cookiesConsentCallbackBound = true;

                /**
                 * Waiting for user consent to eval GTM script
                 * */
                window.waitForUserConsent(data => {
                  if (data.statsAllowed || data.adsAllowed) {
                    eval(script.innerText);
                  }
                });
              }
            } else {
              eval(script.innerText);
            }
          }
        });

        this.loadScriptsIfNeeded();
      }
    }
  };

  public componentWillUnmount() {
    if (this.dispose) {
      this.dispose();
    }
  }

  public componentDidMount() {
    if (this.props.html && !this.props.loading) {
      this.loadScriptsIfNeeded();
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>) {
    const { loading, html } = this.props;

    const hasFinishLoading = !loading && html;
    const htmlHasChanged = html !== prevProps.html;

    if (hasFinishLoading && htmlHasChanged) {
      this.loadScriptsIfNeeded();
    }
  }

  public render() {
    if (this.props.loading || !this.props.html) {
      return (
        <Fragment>
          <LoaderWrapper>
            <Loader size={42} />
          </LoaderWrapper>
          <GlobalStyle />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <GlobalStyle />
        <Raw html={this.props.html} />
      </Fragment>
    );
  }
}
